import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils/styles';
import { NavItem, UserRole } from '@/lib/types/navigation';
import { NAVIGATION_CONFIG } from './constants';
import { LucideIcon } from 'lucide-react';

type SidebarProps = {
  userRole: UserRole;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onLogout?: () => void;
};

export function Sidebar({ 
  userRole, 
  isCollapsed = false, 
  onToggleCollapse, 
  onLogout 
}: SidebarProps) {
  // Ensure the userRole exists in the navigation config, fallback to an empty array
  const navigationItems = (NAVIGATION_CONFIG[userRole] || []) as NavItem[];

  const renderIcon = (icon: string | LucideIcon) => {
    if (typeof icon === 'string') {
      return <span data-testid={`${icon}-icon`} aria-hidden="true">{icon}</span>;
    }
    const Icon = icon;
    return <Icon data-testid={`${icon.name.toLowerCase()}-icon`} aria-hidden="true" />;
  };

  return (
    <aside 
      role="complementary" 
      aria-label="Sidebar navigation" 
      className={cn(
        'bg-background text-foreground border-r border-border',
        'md:flex md:flex-col',
        isCollapsed ? 'w-16' : 'w-64',
        'transition-all duration-300 ease-in-out'
      )}
    >
      <nav aria-label="Main navigation">
        <ul className="space-y-2 p-4">
          {navigationItems.map((item: NavItem) => (
            <li key={item.id}>
              <Link
                to={item.path || ''}
                className={cn(
                  'flex items-center p-2 rounded-md transition-colors',
                  item.badge !== undefined && 'justify-between',
                  'hover:bg-accent hover:text-accent-foreground'
                )}
                aria-current={item.path === window.location.pathname ? 'page' : undefined}
              >
                {renderIcon(item.icon)}
                {!isCollapsed && (
                  <>
                    <span className="ml-3">{item.label}</span>
                    {item.badge !== undefined && (
                      <span className="text-xs font-medium bg-primary text-primary-foreground rounded-full px-2 py-1">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {onToggleCollapse && (
        <button 
          onClick={onToggleCollapse}
          aria-expanded={isCollapsed ? 'false' : 'true'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="mt-auto p-4 self-center"
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </button>
      )}

      {onLogout && (
        <button 
          onClick={onLogout}
          aria-label="Logout"
          className="p-4 self-center"
        >
          Logout
        </button>
      )}
    </aside>
  );
}
